import React from 'react';
import { View, Text, StyleSheet, Modal, Pressable, Platform } from 'react-native';

import { COLORS } from '../../theme/colors';

interface TooltipProps {
  visible: boolean;
  onClose: () => void;
  text: string;
  children: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  accessibilityLabel?: string;
}

/**
 * Tooltip: Simple cross-platform tooltip/popover for accessibility and hints.
 * Usage:
 * <Tooltip visible={show} onClose={hide} text="Info here"> <AnyComponent/> </Tooltip>
 */
const Tooltip: React.FC<TooltipProps> = ({
  visible,
  onClose,
  text,
  children,
  placement = 'top',
  accessibilityLabel
}) => {
  return (
    <View style={{ position: 'relative' }}>
      {children}
      <Modal
        visible={visible}
        transparent
        animationType="fade"
        onRequestClose={onClose}
        accessible={true}
        accessibilityViewIsModal={true}
        accessibilityLabel={accessibilityLabel || 'Tooltip dialog'}
      >
        <Pressable style={styles.overlay} onPress={onClose} accessible={false} />
        <View style={[styles.tooltip, styles[placement]]}>
          <Text style={styles.text}>{text}</Text>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.2)',
    zIndex: 1
  },
  tooltip: {
    position: 'absolute',
    left: '50%',
    top: '10%',
    minWidth: 140,
    maxWidth: 240,
    backgroundColor: COLORS.background,
    borderRadius: 10,
    padding: 12,

    alignSelf: 'center',
    zIndex: 2
  },
  top: { transform: [{ translateY: -70 }] },
  bottom: { transform: [{ translateY: 70 }] },
  left: { left: '10%', right: undefined },
  right: { left: '70%', right: undefined },
  text: {
    color: COLORS.text,
    fontSize: 15,
    textAlign: 'center'
  }
});

export default Tooltip;
